import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, downloadFile } from "@/lib/api";
import { AdminTitle } from "./AdminLayout";
import { BTN } from "./AdminInvoices";
import { ClaimForm, EMPTY_CLAIM, gbp2 } from "./ClaimForm";

const download = (no, fmt) => downloadFile(`/admin/claims/${no}/${fmt}`, `Cirencester-4x4-Claim-${no}.${fmt}`).catch((e) => toast.error(apiError(e)));
const claimed = (c) => c.items.reduce((s, i) => s + (i.net || 0), 0) * (1 + (c.vat_rate || 0) / 100);
const toForm = (c) => ({ ...EMPTY_CLAIM, ...c, items: c.items.map((i) => ({ ...i, net: i.net || "" })) });

const lastLog = (c) => [...c.correspondence].sort((a, b) => b.date.localeCompare(a.date))[0];

const ClaimRow = ({ c, onEdit }) => (
  <li className={`flex flex-wrap items-center justify-between gap-3 border-b border-stone py-4 ${c.status === "Withdrawn" ? "opacity-60" : ""}`} data-testid={`claim-row-${c.claim_no}`}>
    <div className="min-w-0">
      <p className="text-sm font-semibold">{c.claim_no} <span className="font-normal text-charcoal/70">· {c.claim_date} · {c.status}{c.supersedes ? ` · replaces ${c.supersedes}` : ""}</span></p>
      <p className="text-sm">{c.vehicle_description || (c.agreement_no ? "Vehicle from linked purchase" : "Vehicle not specified")}{c.agreement_no ? ` · ${c.agreement_no}` : " · not linked to a purchase"}</p>
      <p className="text-sm font-semibold text-forest">{gbp2(claimed(c))} claimed · {c.items.length} item{c.items.length === 1 ? "" : "s"}</p>
      {c.correspondence?.length > 0 && (
        <p className="text-xs text-charcoal/75" data-testid={`claim-last-contact-${c.claim_no}`}>
          {c.correspondence.length} log entr{c.correspondence.length === 1 ? "y" : "ies"} · last: {lastLog(c).direction.toLowerCase()} {lastLog(c).date}
        </p>
      )}
    </div>
    <div className="flex flex-wrap gap-2">
      <button type="button" className={BTN} onClick={() => download(c.claim_no, "pdf")} data-testid={`claim-pdf-${c.claim_no}`}>Claim PDF</button>
      <button type="button" className={BTN} onClick={() => download(c.claim_no, "docx")} data-testid={`claim-docx-${c.claim_no}`}>Word</button>
      <button type="button" className={BTN} onClick={() => onEdit(c)} data-testid={`claim-edit-${c.claim_no}`}>Edit</button>
    </div>
  </li>
);

export default function AdminClaims() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/claims").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-claims">
      <AdminTitle title="Claims">
        {!editing && <button type="button" onClick={() => setEditing({ initial: EMPTY_CLAIM, claimNo: null })} className="btn btn-primary !py-3" data-testid="claim-new">New claim</button>}
      </AdminTitle>
      <p className="mb-6 text-sm text-charcoal/75">Claims against sellers for undeclared damage or faults. Link a claim to the purchase to fill in the seller and vehicle details, itemise the damage and costs, then download a Notice of Claim (CLM-year-101 onwards) with the photo evidence. Private: admin only.</p>
      {editing && (
        <div className="mb-8">
          <ClaimForm key={editing.claimNo || "new"} initial={editing.initial} claimNo={editing.claimNo}
            onSaved={(c) => { setEditing(null); load(); download(c.claim_no, "pdf"); }} onCancel={() => setEditing(null)} />
        </div>
      )}
      <section className="border border-stone bg-paper px-6 py-2">
        {list.length ? <ul>{list.map((c) => <ClaimRow key={c.claim_no} c={c} onEdit={(x) => { setEditing({ initial: toForm(x), claimNo: x.claim_no }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />)}</ul>
          : <p className="py-6 text-sm text-charcoal/70" data-testid="claims-empty">No claims yet.</p>}
      </section>
    </div>
  );
}
